import { Player } from '@/assets/types'
import { defineStore } from 'pinia'
import { Ref, ref } from 'vue'
import { handleSubscription } from './helper'
import axios from 'axios'


export const usePlayerStore = defineStore('player', () => {
    const player = ref<Player>({
        id: '',
        name: '',
        sessionId: '',
        score: 0,
        label: ''
    })

    // Load from localStorage on init
    const saved = localStorage.getItem('player')
    if (saved) {
        try {
            const parsed = JSON.parse(saved) as Player
            player.value = parsed
        } catch { }
    }



    async function register(name: string, sessionId: string, label: string) {

        const url = new URL(
            `${import.meta.env.VITE_API_ENDPOINT}/players/register`,
            location.href
        )
        return await axios.post(
            url.toString(),
            {
                name: name,
                sessionId: sessionId,
                label: label,
            }
        ).then(
            (res) => {
                editPlayer(res.data)
                return { errMsg: '', player: res.data }
            }
        ).catch(
            (req) => {
                return { errMsg: req.response.data.error, player: null }
            }
        )
    }


    async function submitAnswer(questionIndex: number, answerIndex: number, answerText?: string) {
        const url = new URL(
            `${import.meta.env.VITE_API_ENDPOINT}/answers/submit`,
            location.href

        )
        return await axios.post(
            url.toString(),
            {
                playerId: player.value.id,
                questionIndex: questionIndex,
                answerIndex: answerIndex,
                answerText: answerText ?? '',
            }
        ).then(
            (res) => ({ errMsg: '', answer: res.data })
        ).catch(
            (req) => {
                return { errMsg: req.response.data.error, answer: null }
            }
        )
    }

    async function getPlayerAnswer(questionIndex: number) {
        const url = new URL(
            `${import.meta.env.VITE_API_ENDPOINT}/answers/result`,
            location.href
        )
        url.searchParams.set("playerId", player.value.id)
        url.searchParams.set("questionIndex", String(questionIndex))

        try {
            const res = await axios.get(url.toString())
            return res.data
        } catch {
            return null
        }
    }

    async function resolveToken(pin: string, token: string) {
        const url = new URL(
            `${import.meta.env.VITE_API_ENDPOINT}/session/${pin}/tokens/${token}`,
            location.href
        )
        url.searchParams.set("playerId", player.value.id)

        try {
            const res = await axios.get(url.toString())
            return { data: res.data, error: null, status: res.status }
        } catch (e: any) {
            return {
                data: null,
                error: e.response?.data?.error || 'Unknown error',
                status: e.response?.status || 500,
            }
        }
    }

    async function getPlayerRankByPin(pin: string) {
        const url = new URL(
            `${import.meta.env.VITE_API_ENDPOINT}/session/${pin}/rank/${player.value.id}`,
            location.href
        )
        try {
            const res = await axios.get(url.toString())
            return res.data
        } catch {
            return null
        }
    }

    async function getProgress(pin: string) {
        const url = new URL(
            `${import.meta.env.VITE_API_ENDPOINT}/session/${pin}/progress`,
            location.href
        )
        url.searchParams.set("playerId", player.value.id)

        try {
            const res = await axios.get(url.toString())
            return res.data
        } catch {
            return null
        }
    }

    function reset() {
        localStorage.removeItem('player')
        player.value = {
            id: '',
            name: '',
            sessionId: '',
            score: 0,
            label: '',
        }
    }


    function editPlayer(obj: Player) {
        player.value = obj
        localStorage.setItem(
            'player',
            JSON.stringify(player.value)
        )
    }

    function followPlayers(sessionId: string, arrayRef: Ref<any[]>, counterRef: Ref<number>) {
        return handleSubscription(
            'players',
            {
                create: (doc) => {
                    if (doc.sessionId !== sessionId) return
                    let idx = counterRef.value % 16
                    counterRef.value += 1
                    arrayRef.value[idx] = doc
                },
                update: (doc) => {

                },
                delete: (doc) => {

                },
            }
        )
    }

    async function getPlayers(sessionId: string, limit: number = undefined, options: any = {}) {
        let url = new URL(
            `${import.meta.env.VITE_API_ENDPOINT}/players`,
            location.href
        )

        url.searchParams.set("sessionId", encodeURIComponent(sessionId))
        url.searchParams.set("order", "desc")
        url.searchParams.set("limit", encodeURIComponent(limit))

        Object.entries(options).forEach(([k, v]) => url.searchParams.set(k, encodeURIComponent(v)))

        let res = await axios.get(url.toString(), { withCredentials: true })

        let totalPlayers = res.data ?? []

        url = new URL(
            `${import.meta.env.VITE_API_ENDPOINT}/players/count`,
            location.href
        )

        url.searchParams.set("sessionId", encodeURIComponent(sessionId))

        res = await axios.get(url.toString(), { withCredentials: true })

        let totalCount = res.data?.count ?? 0

        return {
            players: totalPlayers,
            total: totalCount
        }
    }
    async function getAnswers(sessionId: string, options: any = {}) {
        const url = new URL(
            `${import.meta.env.VITE_API_ENDPOINT}/answers`,
            location.href
        )

        url.searchParams.set("sessionId", encodeURIComponent(sessionId))
        url.searchParams.set("order", "desc")

        Object.entries(options).forEach(([k, v]) => url.searchParams.set(k, encodeURIComponent(v)))

        const res = await axios.get(url.toString(), { withCredentials: true })

        let totalPlayers = res.data ?? []
        const totalCount = totalPlayers.length

        return {
            answers: totalPlayers,
            total: totalCount
        }
    }

    async function getPlayersAnswersCount(sessionId: string, questionIndex: number) {
        const url = new URL(
            `${import.meta.env.VITE_API_ENDPOINT}/answers/count`,
            location.href
        )

        url.searchParams.set("sessionId", encodeURIComponent(sessionId))
        url.searchParams.set("questionIndex", encodeURIComponent(questionIndex))
        url.searchParams.set("order", "desc")


        const res = await axios.get(url.toString(), { withCredentials: true })

        return res.data?.count ?? 0
    }

    function followPlayersAnswers(sessionId: string, questionIndex: number, counterRef: Ref<number>) {
        return handleSubscription(
            'answers',
            {
                create: (doc) => {
                    if (doc.sessionId !== sessionId) return
                    if (doc.questionIndex !== questionIndex) return
                    counterRef.value += 1
                },
                update: (doc) => {

                },
                delete: (doc) => {

                },
            }
        )
    }

    return { player, register, reset, editPlayer, submitAnswer, getPlayerAnswer, resolveToken, getPlayerRankByPin, getProgress, followPlayers, getPlayers, followPlayersAnswers, getPlayersAnswersCount, getAnswers }
})
